import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "antd";
import {
  UserOutlined,
  PhoneOutlined,
  LoadingOutlined,
  CheckCircleFilled
} from "@ant-design/icons";
import AuthService, { AuthResponse } from "../../services/auth";
import { REGISTRATION_PLATFORM_MCARE } from "../../constants/registrationPlatform";
import { convertToInternationalFormat } from "../../utils";
import { getAxiosErrorUserMessage } from "../../utils/apiErrorMessage";

/** Shape of the inline onboarding ask returned by
 *  POST /ai-integration/v2/chat/anonymous for MTN campaign guests.
 *  With the ask-at-limit flow, `trigger` is always "forced" (the free
 *  monthly budget is exhausted) — "proactive" is kept for contract
 *  compatibility with the backend DTO. */
export interface CampaignOnboardingPrompt {
  trigger: "proactive" | "forced";
  title: string;
  body: string;
  fields: Array<"fullName" | "phoneNumber">;
  ctaLabel: string;
  /** Campaign badge, e.g. "MTN PULSE" (server-templated). */
  badge?: string;
  /** Free-month benefit checklist (server-templated approved copy). */
  benefits?: string[];
  /** Details the guest volunteered in chat ("proactive" trigger) — rendered
   *  pre-filled so they just confirm. */
  prefill?: { fullName?: string; phoneNumber?: string };
}

/** The PULSE / PRESTIGE coupons live on prod and are managed by the admin
 *  panel (never seeded from code). Only these two campaign sources carry a
 *  coupon — anything else signs up without one. */
function couponCodeForSource(source?: string | null): string | undefined {
  const code = source?.trim().toUpperCase();
  return code === "PULSE" || code === "PRESTIGE" ? code : undefined;
}

interface OnboardingCardProps {
  prompt: CampaignOnboardingPrompt;
  trackingId?: string;
  source?: string | null;
  /** Called with the auth response after a successful verify — the parent
   *  establishes the session (tokens, store, profile) and navigates. */
  onComplete: (
    response: AuthResponse,
    details: { fullname: string; phoneNumber: string }
  ) => Promise<void> | void;
}

type Step = "details" | "otp" | "existing-account";

/** Backend ConflictException text for a number that already has an account
 *  (thrown by both signup-phone and signup-verify). */
const ALREADY_REGISTERED_RE = /already registered/i;

const OnboardingCard: React.FC<OnboardingCardProps> = ({
  prompt,
  trackingId,
  source,
  onComplete
}) => {
  const [step, setStep] = useState<Step>("details");
  const [fullname, setFullname] = useState(prompt.prefill?.fullName ?? "");
  const [phoneNumber, setPhoneNumber] = useState(
    prompt.prefill?.phoneNumber ?? ""
  );
  const [otpCode, setOtpCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const showName = prompt.fields.includes("fullName");
  const showPhone = prompt.fields.includes("phoneNumber");

  const intlPhone = () =>
    `+${convertToInternationalFormat(phoneNumber.trim())}`;

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneNumber.trim()) {
      setError("Enter your phone number to continue.");
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      await AuthService.signUpPhone({
        phoneNumber: intlPhone(),
        fullname: fullname.trim()
      });
      setStep("otp");
    } catch (err) {
      const message = getAxiosErrorUserMessage(err);
      if (ALREADY_REGISTERED_RE.test(message)) {
        setStep("existing-account");
        setError(null);
      } else {
        setError(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otpCode.trim().length < 6) {
      setError("Enter the 6-digit code we sent you.");
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      const couponCode = couponCodeForSource(source);
      const response = await AuthService.signUpVerify({
        phoneNumber: intlPhone(),
        otpCode: otpCode.trim(),
        fullname: fullname.trim() || undefined,
        ...(couponCode && { couponCode, couponApplied: true }),
        trackingId,
        registrationPlatform: REGISTRATION_PLATFORM_MCARE
      });
      await onComplete(response, {
        fullname: fullname.trim(),
        phoneNumber: phoneNumber.trim()
      });
    } catch (err) {
      const message = getAxiosErrorUserMessage(err);
      if (ALREADY_REGISTERED_RE.test(message)) {
        setStep("existing-account");
        setError(null);
      } else {
        setError(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    // MTN campaign styling (products, 2026-08-20): black card, yellow badge,
    // positive headline, benefit checklist — scoped to the card; the page
    // keeps mcare branding.
    <div className="max-w-[85%] sm:max-w-[75%] rounded-2xl rounded-bl-md overflow-hidden shadow-md bg-neutral-900 text-white">
      <div className="px-4 pt-4 pb-2 space-y-2">
        {prompt.badge && (
          <span className="inline-block rounded-md bg-[#FFCC08] px-2.5 py-0.5 text-[10px] font-bold tracking-wider text-black">
            {prompt.badge}
          </span>
        )}
        <p className="font-semibold text-base m-0">{prompt.title}</p>
        <p className="text-xs text-white/70 m-0">{prompt.body}</p>
        {!!prompt.benefits?.length && (
          <ul className="m-0 list-none p-0 pt-1 space-y-1.5">
            {prompt.benefits.map((benefit) => (
              <li
                key={benefit}
                className="flex items-center gap-2 text-xs text-white/90"
              >
                <CheckCircleFilled className="text-[#FFCC08]" />
                {benefit}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="px-4 py-3 space-y-3">
        {step === "existing-account" ? (
          <div className="space-y-2.5">
            <p className="text-sm text-white/90 m-0">
              Good news — {phoneNumber.trim() || "this number"} already has an
              account. Log in to keep chatting with Kobikuul and use your
              benefits.
            </p>
            <Link
              to="/sign-in"
              className="block w-full rounded-lg bg-[#FFCC08] px-4 py-2 text-center text-sm font-semibold text-black hover:bg-[#e6b807] hover:text-black transition-colors"
            >
              Log in with this number
            </Link>
            <button
              type="button"
              onClick={() => {
                setPhoneNumber("");
                setOtpCode("");
                setError(null);
                setStep("details");
              }}
              className="w-full rounded-lg border border-white/30 px-4 py-2 text-sm font-medium text-white hover:bg-white/10 transition-colors"
            >
              Use a different number
            </button>
          </div>
        ) : step === "details" ? (
          <form onSubmit={handleSendOtp} className="space-y-2.5">
            {showName && (
              <Input
                prefix={<UserOutlined className="text-neutral-400" />}
                value={fullname}
                onChange={(e) => setFullname(e.target.value)}
                placeholder="Your name (or an alias)"
                disabled={isSubmitting}
              />
            )}
            {showPhone && (
              <Input
                prefix={<PhoneOutlined className="text-neutral-400" />}
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Phone number"
                type="tel"
                disabled={isSubmitting}
              />
            )}
            {error && <p className="text-xs text-red-400 m-0">{error}</p>}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-lg bg-[#FFCC08] px-4 py-2 text-sm font-semibold text-black hover:bg-[#e6b807] transition-colors disabled:opacity-50"
            >
              {isSubmitting ? <LoadingOutlined /> : prompt.ctaLabel}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className="space-y-2.5">
            <p className="text-xs text-white/70 m-0">
              Enter the code we texted to {phoneNumber}.
            </p>
            <Input
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value)}
              placeholder="6-digit code"
              inputMode="numeric"
              maxLength={6}
              disabled={isSubmitting}
            />
            {error && <p className="text-xs text-red-400 m-0">{error}</p>}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-lg bg-[#FFCC08] px-4 py-2 text-sm font-semibold text-black hover:bg-[#e6b807] transition-colors disabled:opacity-50"
            >
              {isSubmitting ? <LoadingOutlined /> : "Confirm & continue"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default OnboardingCard;
